import { MappingType_Enum } from '@weave/schema-gen-ts/dist/schemas/syncapp/mappings/v1/service.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { BannerNotification, Button, Text } from '@frontend/design-system';

type Props = {
  locationId: string | undefined;
  sourceId: string | undefined;
  locationName: string | undefined;
  pmsName: string | undefined;
};

export const AppointmentStatusMappingBanner = ({
  locationId = '',
  sourceId = '',
  locationName = '',
  pmsName = '',
}: Props) => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const { isSingleTypeScope, accessibleLocationData } = useAppScopeStore();
  const { navigate: settingsNavigate } = useSettingsNavigate();

  // Data sources and their status mappings are defined on the parent location
  const parentLocationId = isSingleTypeScope ? locationId : accessibleLocationData[locationId]?.groupId || locationId;

  const { data: appointmentStatusMapping, isSuccess } = DataSourcesQueries.useSourceMappingsList(
    sourceId,
    { locationId: parentLocationId, type: MappingType_Enum.APPOINTMENT },
    { enabled: !!sourceId && !!parentLocationId }
  );

  const hasAppointmentStatusMapping = !!appointmentStatusMapping?.mappings?.some(({ value }) => !!value?.length);

  if (!sourceId || !parentLocationId || !isSuccess || hasAppointmentStatusMapping) return null;

  const handleMapAppointmentStatus = () => {
    settingsNavigate({
      to: '/organization/data-sync/location/:locationId/source/:sourceId',
      params: { locationId: parentLocationId, sourceId },
      search: { name: pmsName, flag: false },
    });
  };

  return (
    <BannerNotification
      status='warn'
      css={{ marginBottom: 'var(--fabric-space-gap-lg)', marginTop: 'calc(-1 * var(--fabric-space-gap-md))' }}
    >
      <div>
        <Text weight='bold'>{t('Appointment Status mapping is missing')}</Text>
        <Text>
          {t('{{location}} has {{pms}} appointment statuses that are not mapped to Weave statuses.', {
            location: locationName,
            pms: pmsName,
          })}
        </Text>
        <Button onClick={handleMapAppointmentStatus} variant='tertiary' css={{ padding: 0 }}>
          {t('Map Appointment Status')}
        </Button>
      </div>
    </BannerNotification>
  );
};
